/*
 * TEEN SELF-HOLDER — the Get ready "Parent/guardian signature needed" card
 * state (VITE_TEEN_SELF_HOLDER_ENABLED; design MY-DESIGN-JUNIOR-SELF-HOLDER-
 * 1020 §4.3.2 / §4.7; ruling BOARDROOM-2026-09-07-103021 card composition).
 *
 * SERVER TRUTH: my-api readiness `guardianSignatureRequired` (PR1) says a
 * guardian signature is still owed, and readiness `guardianRequest` (the
 * follow-up after #418) carries the latest request — state, expiry and the
 * parent's NAME — so the card is right on any device. THIS DEVICE still keeps
 * a small per-ticket record of the last reply plus the parent's NAME, used
 * only when the server carry is absent (an older my-api or a degraded read).
 * The parent's EMAIL is never stored (contact data stays on the server).
 */
import { MyApiError, type GuardianRequestState, type GuardianRequestView } from './myApi'
import type { TranslateFn } from '../i18n/useLocale'

export interface TeenGuardianRecord {
  requestId: string
  state: GuardianRequestState
  expiresAt: string
  /** The parent's name as the teen typed it — '' when unknown. */
  parentName: string
}

const KEY = 'my.teenGuardianRequests'

function readAll(): Record<string, TeenGuardianRecord> {
  try {
    const raw = localStorage.getItem(KEY)
    const v = raw ? (JSON.parse(raw) as unknown) : null
    return v && typeof v === 'object' ? (v as Record<string, TeenGuardianRecord>) : {}
  } catch {
    return {}
  }
}

function writeAll(all: Record<string, TeenGuardianRecord>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all))
  } catch {
    /* storage unavailable — the card falls back to the server's flag only */
  }
}

export function teenGuardianRecord(ticketId: string): TeenGuardianRecord | null {
  const r = readAll()[ticketId]
  return r && typeof r.requestId === 'string' ? r : null
}

/** Record a request route's reply (create / change / resend). */
export function recordTeenGuardianRequest(ticketId: string, view: Partial<GuardianRequestView>, parentName?: string): void {
  const all = readAll()
  const prev = all[ticketId]
  all[ticketId] = {
    requestId: view.requestId ?? prev?.requestId ?? '',
    state: view.state ?? prev?.state ?? 'requested',
    expiresAt: view.expiresAt ?? prev?.expiresAt ?? '',
    parentName: (parentName ?? prev?.parentName ?? '').trim(),
  }
  writeAll(all)
}

export function clearTeenGuardianRecordsForTests(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* no-op */
  }
}

export type TeenGuardianCardState = 'ask' | 'waiting' | 'signed'

export interface TeenGuardianCard {
  ticketId: string
  state: TeenGuardianCardState
  parentName: string
  /** Ask state after a request expired: "Link expired — send again". */
  expired?: boolean
}

const live = (s: GuardianRequestState | undefined): boolean => s === 'requested' || s === 'verified'

/**
 * The card state for ONE kind='self' holding (flag on). null ⇒ no teen
 * treatment (the plain waiver step stays, byte-identical).
 *  - required (server flag) + a live, unexpired request → 'waiting';
 *  - required + an expired request → 'ask' with expired;
 *  - required + no request → 'ask';
 *  - not required + waiver complete + a server 'signed' request or a recorded
 *    request → 'signed'. The parent's name comes from the server carry first.
 */
export function teenGuardianCardState(input: {
  ticketId: string
  required: boolean
  waiverComplete: boolean
  serverRequest?: GuardianRequestView | null
  local?: TeenGuardianRecord | null
  now?: Date
}): TeenGuardianCard | null {
  const now = (input.now ?? new Date()).getTime()
  const local = input.local ?? null
  const server = input.serverRequest ?? null
  const req = server ?? local
  const parentName = (server?.guardianName ?? '').trim() || local?.parentName || ''
  if (input.required) {
    if (req && live(req.state)) {
      const exp = Date.parse(req.expiresAt)
      if (!Number.isNaN(exp) && exp <= now) return { ticketId: input.ticketId, state: 'ask', parentName, expired: true }
      return { ticketId: input.ticketId, state: 'waiting', parentName }
    }
    if (req && req.state === 'expired') return { ticketId: input.ticketId, state: 'ask', parentName, expired: true }
    return { ticketId: input.ticketId, state: 'ask', parentName }
  }
  if (input.waiverComplete && (server?.state === 'signed' || local)) return { ticketId: input.ticketId, state: 'signed', parentName }
  return null
}

/** Plain-language copy for a request-route failure (the 409/429 caps). */
export function guardianRequestErrorCopy(
  e: unknown,
  tr: TranslateFn,
  formatTime: (iso: string) => string = (iso) => new Date(iso).toLocaleString(),
): string {
  if (!(e instanceof MyApiError)) return tr('teenGuardian.error.generic')
  const body = e.body ?? {}
  switch (e.code) {
    case 'guardian_request_change_limit': return tr('teenGuardian.error.changeLimit')
    case 'guardian_resend_too_soon': {
      const at = typeof body.retryAt === 'string' ? body.retryAt : null
      return at ? tr('teenGuardian.error.resendTooSoonAt', { time: formatTime(at) }) : tr('teenGuardian.error.resendTooSoon')
    }
    case 'guardian_request_expired': return tr('teenGuardian.error.expired')
    case 'guardian_request_not_found': return tr('teenGuardian.error.notFound')
    case 'guardian_request_exists': return tr('teenGuardian.error.exists')
    case 'already_signed': return tr('teenGuardian.error.alreadySigned')
    case 'invalid_email': return tr('teenGuardian.error.email')
    case 'invalid_input': return tr('teenGuardian.error.input')
    case 'guardian_request_not_allowed':
      if (e.reason === 'guardian_is_holder') return tr('teenGuardian.error.ownEmail')
      if (e.reason === 'under_16') return tr('teenGuardian.error.under16')
      if (e.reason === 'adult_holder') return tr('teenGuardian.error.adult')
      return e.serverMessage ?? tr('teenGuardian.error.generic')
    case 'ineligible':
      if (e.reason === 'dob_required') return tr('teenGuardian.error.dobRequired')
      return e.serverMessage ?? tr('teenGuardian.error.generic')
    default:
      if (e.status === 429) return tr('teenGuardian.error.resendTooSoon')
      if (e.status === 503) return tr('teenGuardian.error.unavailable')
      return e.serverMessage ?? tr('teenGuardian.error.generic')
  }
}
